import React, { use, createContext, useContext, useMemo } from 'react';
import { DEFAULT_LOCALE, LOCALES } from '../i18n/config';
import { useI18n } from '../i18n/LanguageContext';
import { POST_IDS, idFromSlug } from './slugs';

/**
 * Article and legal copy for one locale is roughly ten times the size of the
 * UI strings, and a visitor only ever reads one language. Each locale is its
 * own webpack chunk, fetched on demand, so nobody downloads seven copies of
 * the blog. Static explicit imports (not a template literal) so the bundler
 * can name and split every chunk.
 */
const loaders = {
  en: () => import('./en'),
  tr: () => import('./tr'),
  es: () => import('./es'),
  fr: () => import('./fr'),
  de: () => import('./de'),
  it: () => import('./it'),
  nl: () => import('./nl'),
};

/** Promises are cached so `use()` sees a stable promise across re-renders. */
const cache = new Map();

export function loadContent(locale) {
  const key = LOCALES.includes(locale) ? locale : DEFAULT_LOCALE;
  if (!cache.has(key)) {
    cache.set(
      key,
      loaders[key]().then((mod) => mod.default)
    );
  }
  return cache.get(key);
}

const ContentContext = createContext(null);

/** Suspends until the active locale's content chunk has loaded. */
export function ContentProvider({ children }) {
  const { locale } = useI18n();
  const content = use(loadContent(locale));

  const value = useMemo(() => {
    const byId = new Map(content.posts.map((p) => [p.id, p]));

    const getPostById = (id) => byId.get(id) || null;

    const getPostBySlug = (slug) => {
      const id = idFromSlug(slug, locale);
      return id ? getPostById(id) : null;
    };

    /** Same-category posts first, then the rest, so "keep reading" stays relevant. */
    const getRelatedPosts = (id, count = 3) => {
      const current = getPostById(id);
      if (!current) return content.posts.slice(0, count);
      const sameCategory = content.posts.filter(
        (p) => p.id !== id && p.category === current.category
      );
      const others = content.posts.filter((p) => p.id !== id && p.category !== current.category);
      return [...sameCategory, ...others].slice(0, count);
    };

    return {
      locale,
      posts: content.posts,
      terms: content.terms,
      privacy: content.privacy,
      getPostById,
      getPostBySlug,
      getRelatedPosts,
    };
  }, [content, locale]);

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}

export function useContent() {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error('useContent must be used inside a ContentProvider');
  return ctx;
}

export { POST_IDS };
